import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import BaseModal, { modalStyles, HoverButton } from './BaseModal';
import ModalStickyFooter from '../ui/ModalStickyFooter';
import { FontAwesome6 } from '@expo/vector-icons';
import { colors, typography } from '../../styles/theme';

interface TermsOfUseProps {
  visible: boolean;
  onClose: () => void;
  onBack: () => void;
}

export default function TermsOfUseModal({ visible, onClose, onBack }: TermsOfUseProps) {
  return (
    <BaseModal
      visible={visible}
      onClose={onClose}
      header={(
        <View style={styles.headerRow}>
          <TouchableOpacity onPress={onBack} style={styles.backButton}>
            <FontAwesome6 name="arrow-left" size={20} color={colors.text} solid />
          </TouchableOpacity>
          <Text style={[modalStyles.headerText, { flex: 1, marginBottom: 0 }]}>Terms of Use</Text>
          <View style={{ width: 44 }} />
        </View>
      )}
      footer={(
        <ModalStickyFooter>
          <HoverButton
            style={modalStyles.secondaryButton}
            textStyle={modalStyles.secondaryButtonText}
            onPress={onClose}
          >
            Close
          </HoverButton>
        </ModalStickyFooter>
      )}
    >
      <View style={styles.container}>
        <Text style={styles.lastUpdated}>Last Updated: May 2026</Text>

        <Text style={styles.introParagraph}>
          By accessing or using Piclo (the "Service"), you agree to be bound by these Terms of Use.
        </Text>

        <Text style={styles.sectionHeader}>1. Service Provided "As-Is"</Text>
        <Text style={styles.paragraph}>
          Piclo is provided on an "as is" and "as available" basis. Because the application relies heavily on device-specific web browser implementations of camera hardware (WebRTC), we cannot guarantee flawless performance across all mobile devices, operating systems, or browsers. We reserve the right to modify, suspend, or discontinue the Service at any time without notice.
        </Text>

        <Text style={styles.sectionHeader}>2. Purchases and Digital Entitlements</Text>
        <Text style={styles.paragraph}>
          Premium puzzle packs and the "Piclo Play" subscription are sold as digital entitlements.
        </Text>
        <View style={styles.bulletContainer}>
          <Text style={styles.bulletItem}>
            • <Text style={styles.boldText}>Access:</Text> Purchases are tied to the email address used at checkout. You can restore your purchases on other devices by requesting a magic link to that email address.
          </Text>
          <Text style={styles.bulletItem}>
            • <Text style={styles.boldText}>Merchant of Record:</Text> All payments, taxes, and refunds are processed by our authorized reseller, Paddle.com.
          </Text>
        </View>

        <Text style={styles.sectionHeader}>3. Refund Policy</Text>
        <Text style={styles.paragraph}>
          If you experience severe technical issues that prevent you from utilizing a purchased puzzle pack, please contact us at support@piclo.art within 14 days of purchase. Approved refunds will be processed through Paddle.com and returned to your original payment method.
        </Text>

        <Text style={styles.sectionHeader}>4. User Generated Content and Copyright</Text>
        <Text style={styles.paragraph}>
          Any images you capture while using Piclo remain your sole property. We claim no ownership over the photos you take or the final composite artwork you generate and export from the application.
        </Text>
        <Text style={styles.paragraph}>
          The puzzle templates, SVG assets, and original game designs provided within the Service are the intellectual property of Piclo and may not be extracted, redistributed, or sold without explicit permission.
        </Text>

        <Text style={styles.sectionHeader}>5. Limitation of Liability</Text>
        <Text style={styles.paragraph}>
          To the maximum extent permitted by law, Piclo and its creators shall not be liable for any indirect, incidental, or consequential damages arising from your use of the Service, including but not limited to device performance issues or data loss resulting from cleared browser caches.
        </Text>
      </View>
    </BaseModal>
  );
}

const styles = StyleSheet.create({
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    width: '100%',
  },
  backButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    marginBottom: 24,
  },
  lastUpdated: {
    ...typography.body,
    fontSize: 14,
    color: colors.textMuted,
    fontStyle: 'italic',
    marginBottom: 16,
  },
  introParagraph: {
    ...typography.bodyBold,
    color: colors.text,
    fontSize: 16,
    lineHeight: 22,
    marginBottom: 20,
  },
  sectionHeader: {
    ...typography.bodyBold,
    fontSize: 18,
    color: colors.text,
    marginTop: 16,
    marginBottom: 8,
  },
  paragraph: {
    ...typography.body,
    color: colors.textMuted,
    fontSize: 15,
    lineHeight: 22,
    marginBottom: 12,
    textAlign: 'left',
  },
  bulletContainer: {
    marginLeft: 8,
    marginBottom: 16,
  },
  bulletItem: {
    ...typography.body,
    color: colors.textMuted,
    fontSize: 15,
    lineHeight: 22,
    marginBottom: 8,
  },
  boldText: {
    ...typography.bodyBold,
    color: colors.text,
  },
});
